import { ChevronLeft, Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import { Button } from "@components/ui/button";
import SimpleBar from "simplebar-react";
import { Textarea } from "@components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import "simplebar-react/dist/simplebar.min.css";
import {
  getVpsProfileById,
  updateVpsProfile,
  VpsProfile,
} from "@redux/slices/strategicPlanning";
import useAuth from "@hooks/useAuth";
import LoadingScreen from "@components/LoadingScreen";
import { Input } from "@components/ui/input";
import {
  addNotification,
} from "@redux/slices/notifications";

const EditVps: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [id, setId] = useState("");

  const [formData, setFormData] = useState<VpsProfile>({
    user_id: user?.id,
    user_name: user?.first_name + " " + user?.last_name,
    status: "draft",
    name: "",
    question1: "",
    question2: "",
    question3: "",
    question4: "",
    question5: "",
    question6: "",
    question7: "",
    question8: "",
    question9: "",
    question10: "",
  });

  const icpSchema = z.object({
    question1: z.string(),
    name: z.string(),
    question2: z.string(),
    question3: z.string(),
    question4: z.string(),
    question5: z.string(),
    question6: z.string(),

    question7: z.string(),
    question8: z.string(),
    question9: z.string(),
    question10: z.string(),
  });

  type ICPFormData = z.infer<typeof icpSchema>;

  const {
    register,
    formState: { errors },
    reset,
  } = useForm<ICPFormData>({
    resolver: zodResolver(icpSchema),
    mode: "onBlur",
    defaultValues: formData,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const id = Number(window.location.pathname.split("/")[3]);
      const data = await getVpsProfileById(id);
      console.log(data);
      setFormData({
        user_id: data.user_id,
        user_name: data.user_name,
        name: data.name,
        status: data.status,
        question1: data.question1,
        question2: data.question2,
        question3: data.question3,
        question4: data.question4,
        question5: data.question5,
        question6: data.question6,
        question7: data.question7,
        question8: data.question8,
        question9: data.question9,
        question10: data.question10,
      });
      reset({
        name: data.name,
        question1: data.question1,
        question2: data.question2,
        question3: data.question3,
        question4: data.question4,
        question5: data.question5,
        question6: data.question6,
        question7: data.question7,
        question8: data.question8,
        question9: data.question9,
        question10: data.question10,
      });
      setLoading(false);
      console.log("aS", formData);
    };
    fetchData();
  }, [reset]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const updateProfile = async () => {
    setLoading(true);
    let updatedFormData = { ...formData };
    if (
      formData?.name != "" &&
      formData?.question1 != "" &&
      formData.question2 != "" &&
      formData.question3 != "" &&
      formData.question4 != "" &&
      formData.question5 != "" &&
      formData.question6 != "" &&
      formData.question7 != "" &&
      formData.question8 != "" &&
      formData.question9 != "" &&
      formData.question10 != ""
    ) {
      console.log("first");
      updatedFormData = {
        ...updatedFormData,
        status: "completed",
      };

      await addNotification("VPS Completed", formData.user_name+" Completed Profile !");
    
    } else {
      updatedFormData = {
        ...updatedFormData,
        status: "draft",
      };
    }
    await updateVpsProfile(
      Number(window.location.pathname.split("/")[3]),
      updatedFormData,
      "Profile updated Successfully"
    );
    navigate(`/strategicPlanning/vps/${user?.id}`);
    setLoading(false);
  };

  const questions = [
    "What specific advantages does your product/service offer that your customers cannot find anywhere else?",
    "What are the top three reasons your customers chose your product/service?",
    "How do your products/services contribute to achieving your customers' strategic goals?",
    "What positive changes have your customers experienced by using your products/services?",
    "How would your customers describe the value they receive from your product/service?",
    "What problems or challenges did your customers face before using your solution, and how were these resolved by your product/service?",
    "How does your product/service stand out in terms of quality and performance compared to the competition?",
    "What aspects of your customer service or support are most appreciated by your customers?",
    "What future developments or improvements do you plan to further increase the value of your product/service?",
    "How do you communicate the benefits and advantages of your product/service to your potential and existing customers?",
  
  ];


  const downloadClicked = () => {
    // Format questions and answers
    const formattedContent = questions
      .map((question, index) => {
        const answerKey = `question${index + 1}` as keyof VpsProfile; // Matches keys in formData
        const answer = formData[answerKey] || "No answer provided";
        return `Q${index + 1}: ${question}\nA${index + 1}: ${answer}\n\n`;
      })
      .join("");

    // Create a Blob
    const blob = new Blob([formattedContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    // Trigger download
    const a = document.createElement("a");
    a.href = url;
    a.download = "VPS-Profile.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up URL
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-xl font-bold flex items-center -ms-2">
              <ChevronLeft
                size={22}
                className="me-3 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              {user?.role !== "admin"
                ? " Update Value Propositions and Strengths (VPS)"
                : formData?.user_name}
            </h4>
            <div className="flex items-center">
              <div className="flex items-start mr-5">
                <p className="mt-2">Profile Name: </p>
                <div>
                  <Input
                    {...register("name")}
                    className="w-full ms-2"
                    placeholder="Profile Name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    // value={}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm ms-2 mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>
              </div>
              {user?.role == "admin" && (
                <Button
                  variant="secondary"
                  className=" bg-white"
                  onClick={() => downloadClicked()}
                  // onClick={() =>
                  //   navigate(`/strategicPlanning/readmeBma/${window.location.pathname.split("/")[2]}`)
                  // }
                >
                  <Download size={20} className="me-2 "  />
                  Download
                </Button>
              )}
            </div>
          </div>

          {/* Form Container */}
          <div className="px-56 mt-10">
            <Box sx={{ width: "100%" }}>
              <SimpleBar style={{ maxHeight: 450 }}>
                <div className="px-5">
                  {/* Question 1 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      1. What specific advantages does your product/service
                      offer that your customers cannot find anywhere else?
                    </p>
                    <Textarea
                      {...register("question1")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question1}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question1 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question1.message}
                      </p>
                    )}
                  </div>
                  {/* Question 2 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      2. What are the top three reasons your customers chose
                      your product/service?
                    </p>
                    <Textarea
                      {...register("question2")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question2}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question2 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question2.message}
                      </p>
                    )}
                  </div>
                  {/* Question 3 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      3. How do your products/services contribute to achieving
                      your customers' strategic goals?
                    </p>
                    <Textarea
                      {...register("question3")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question3}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question3 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question3.message}
                      </p>
                    )}
                  </div>
                  {/* Question 4 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      4. What positive changes have your customers experienced
                      by using your products/services?
                    </p>
                    <Textarea
                      {...register("question4")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question4}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question4 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question4.message}
                      </p>
                    )}
                  </div>
                  {/* Question 5 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      5. How would your customers describe the value they
                      receive from your product/service?
                    </p>
                    <Textarea
                      {...register("question5")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question5}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question5 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question5.message}
                      </p>
                    )}
                  </div>
                  {/* Question 6 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      6. What problems or challenges did your customers face
                      before using your solution, and how were these resolved by
                      your product/service?
                    </p>
                    <Textarea
                      {...register("question6")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question6}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question6 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question6.message}
                      </p>
                    )}
                  </div>
                  {/* Question 7 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      7. How does your product/service stand out in terms of
                      quality and performance compared to the competition?
                    </p>
                    <Textarea
                      {...register("question7")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question7}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question7 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question7.message}
                      </p>
                    )}
                  </div>
                  {/* Question 8 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      8. What aspects of your customer service or support are
                      most appreciated by your customers?
                    </p>
                    <Textarea
                      {...register("question8")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question8}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question8 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question8.message}
                      </p>
                    )}
                  </div>
                  {/* Question 9 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      9. What future developments or improvements do you plan to
                      further increase the value of your product/service?
                    </p>
                    <Textarea
                      {...register("question9")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question9}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question9 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question9.message}
                      </p>
                    )}
                  </div>
                  {/* Question 10 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      10. How do you communicate the benefits and advantages of
                      your product/service to your potential and existing
                      customers?
                    </p>
                    <Textarea
                      {...register("question10")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question10}
                      onChange={handleInputChange}
                      disabled={user?.role == "admin" && true}
                    />
                    {errors.question10 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question10.message}
                      </p>
                    )}
                  </div>
                </div>
              </SimpleBar>
              <div className="flex justify-end mt-5">
                {user?.role !== "admin" && (
                  <Button onClick={updateProfile}>Save</Button>
                )}
              </div>
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVps;
